import React, { useState, useEffect, useRef } from "react"
import "./HomeTopSection.scss"
import { Swiper, SwiperSlide } from "swiper/react";
import { breakpointDt, breakpointDt_mid } from "../../../../constants/constants";
import { useNavigate } from "react-router-dom";
import SwiperCore, { Pagination, Autoplay, EffectFade } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

SwiperCore.use([Pagination, Autoplay, EffectFade]);

interface IProps {
    device: "MB" | "TB" | "DT"
    data: {
        random_order: boolean,
        modify_date: string,
        is_auto_slide_enabled: boolean
        contents: [
            {
                asset: { mobile: string, desktop: string },
                title: { text: string, font_color: string },
                button: { background_color: string, font_color: string, text: string, link: string, is_outlined: boolean, outline_color: string },
                order: number;
                is_gradient_enabled: boolean;
                auto_slide_interval: number;
            }
        ]
    }
}

function HomeTopSection(props: IProps) {
    const { data, device } = props
    console.log(data)
    const swiperRef = useRef<SwiperCore>();
    const [content, setContent] = useState([])
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const progressAnimationRef = useRef<number | null>(null);
    const startTimeRef = useRef<number>(0);
    const isInitialized = useRef(false);

    const navigate = useNavigate()


    useEffect(() => {
        if (data) {
            if (data.random_order) {
                const shuffled = data.contents.sort(() => Math.random() - 0.5);
                setContent([...shuffled])
            } else {
                const sortArr = data.contents.sort((a, b) => a.order - b.order)
                setContent([...sortArr])
            }
        }
    }, [data])

    // 페이지 가시성 변경 감지 (iOS 백그라운드 대응)
    useEffect(() => {
        if (!data) return;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && swiperRef.current && isInitialized.current && data?.is_auto_slide_enabled) {
                const currentIndex = swiperRef.current.realIndex;
                // 페이지가 다시 보이면 타이머와 애니메이션 재시작
                setTimeout(() => {
                    const swiper = swiperRef.current!;
                    startTimer(swiper, currentIndex);
                    restartProgressAnimation(currentIndex, () => {
                        moveToNextSlide(swiper, currentIndex);
                    });
                }, 100);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
    }, [data?.is_auto_slide_enabled])

    // 컴포넌트 언마운트 시 타이머와 애니메이션 정리
    useEffect(() => {
        return () => {
            resetTimer();
            if (progressAnimationRef.current) {
                cancelAnimationFrame(progressAnimationRef.current);
            }
        }
    }, [])


    // SVG progress ring 애니메이션 재시작 함수
    const restartProgressAnimation = (slideIndex: number, onComplete?: () => void) => {
        // 기존 애니메이션 취소
        if (progressAnimationRef.current) {
            cancelAnimationFrame(progressAnimationRef.current);
            progressAnimationRef.current = null;
        }

        const bullets = document.querySelectorAll('.swiper-pagination .swiper-pagination-bullet');

        // 모든 불렛의 애니메이션 초기화
        bullets.forEach((bullet, i) => {
            const progressRing = bullet.querySelector('.progress-ring') as HTMLElement;
            const fg = progressRing?.querySelector('.fg') as HTMLElement;

            if (fg) {
                // 모든 불렛을 초기 상태로 리셋
                fg.style.transition = 'none';
                fg.style.strokeDashoffset = '50.24';
                void fg.offsetWidth; // 리플로우 유도
            }
        });

        // 현재 활성 불렛만 애니메이션 시작
        const currentBullet = bullets[slideIndex] as HTMLElement;
        if (currentBullet) {
            const progressRing = currentBullet.querySelector('.progress-ring') as HTMLElement;
            const fg = progressRing?.querySelector('.fg') as HTMLElement;

            if (fg) {
                const duration = (content[slideIndex]?.auto_slide_interval || 5) * 1000;
                const totalLength = 50.24; // 2πr (r=8)
                const startValue = totalLength;
                const endValue = 0;

                // iOS에서도 동작하도록 requestAnimationFrame 사용
                startTimeRef.current = performance.now();
                
                const animate = (currentTime: number) => {
                    const elapsed = currentTime - startTimeRef.current;
                    const progress = Math.min(elapsed / duration, 1);
                    
                    const currentValue = startValue - (startValue - endValue) * progress;
                    fg.style.strokeDashoffset = currentValue.toString();
                    fg.style.opacity = '1';

                    if (progress < 1) {
                        progressAnimationRef.current = requestAnimationFrame(animate);
                    } else {
                        fg.style.strokeDashoffset = '0';
                        progressAnimationRef.current = null;
                        // 애니메이션 완료 시 콜백 실행
                        if (onComplete) {
                            onComplete();
                        }
                    }
                };

                // 초기 상태 설정
                fg.style.transition = 'none';
                fg.style.strokeDashoffset = startValue.toString();
                fg.style.opacity = '1';
                void fg.offsetWidth; // 리플로우 유도

                // iOS에서도 동작하도록 약간의 지연 후 애니메이션 시작
                setTimeout(() => {
                    // 다시 확인하여 요소가 여전히 존재하는지 확인
                    const checkFg = currentBullet.querySelector('.progress-ring .fg') as HTMLElement;
                    if (checkFg && !progressAnimationRef.current) {
                        startTimeRef.current = performance.now();
                        progressAnimationRef.current = requestAnimationFrame(animate);
                    }
                }, 50);
            }
        }
    }

    // 영상 재생/정지 함수
    const handleVideoPlayback = (realIndex: number) => {
        const currentContent = content[realIndex];
        console.log('Current content:', currentContent);

        if (!currentContent) return;

        // 현재 디바이스에 맞는 에셋 확인
        const isVideo = device === "DT"
            ? currentContent.asset.desktop.includes('mp4')
            : currentContent.asset.mobile.includes('mp4');

        if (!isVideo) {
            console.log('Content is not video');
            return;
        }

        console.log('Content is video, processing...');

        // 모든 영상 일시정지 및 초기화
        const allVideos = document.querySelectorAll('.swiper-wrapper video');
        allVideos.forEach((video) => {
            const videoElement = video as HTMLVideoElement;
            videoElement.pause();
            videoElement.currentTime = 0;
        });

        // 현재 슬라이드의 영상 찾기 - 더 간단한 방법
        const currentSlide = document.querySelector(`.swiper-slide:nth-child(${realIndex + 1})`);
        let targetVideo = null;

        if (currentSlide) {
            targetVideo = currentSlide.querySelector('video') as HTMLVideoElement;
        }

        // 위 방법이 실패하면 ID로 찾기
        if (!targetVideo) {
            targetVideo = document.getElementById(`video_${realIndex}`) as HTMLVideoElement;
        }

        if (targetVideo) {
            console.log('Found target video:', targetVideo.src);

            // 영상 설정
            targetVideo.currentTime = 0;
            targetVideo.muted = true;
            targetVideo.playsInline = true;
            targetVideo.loop = true;

            // 영상 재생 함수
            const playVideo = async () => {
                try {
                    console.log('Attempting to play video...');
                    await targetVideo.play();
                    console.log('Video played successfully');
                } catch (err) {
                    console.log('Play error:', err);
                    // 재시도 로직
                    setTimeout(async () => {
                        try {
                            await targetVideo.play();
                            console.log('Retry successful');
                        } catch (retryErr) {
                            console.log('Retry failed:', retryErr);
                        }
                    }, 100);
                }
            };

            // 즉시 재생 시도
            playVideo();

            // 추가 재시도 (특히 첫 번째 영상)
            setTimeout(() => {
                if (targetVideo.paused) {
                    console.log('Video still paused, retrying...');
                    playVideo();
                }
            }, 200);

        } else {
            console.log('No video found for current content');
        }
    }

    // 타이머 리셋 함수
    const resetTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }

    // 슬라이드 이동 함수
    const moveToNextSlide = (swiper: SwiperCore, realIndex: number) => {
        if (swiper && !swiper.destroyed) {
            if (realIndex === content.length - 1) {
                // 마지막 슬라이드에서 첫 번째로
                try {
                    swiper.slideTo(0, 300);
                } catch (error) {
                    // 에러 무시
                }
            } else {
                // 다음 슬라이드로
                try {
                    swiper.slideNext();
                } catch (error) {
                    // 에러 무시
                }
            }
        }
    }

    // 다음 슬라이드로 이동하는 타이머 설정 (iOS 호환성 개선)
    // progress ring 애니메이션 완료 시점에 슬라이드를 넘기므로 타이머는 백업용으로만 사용
    const startTimer = (swiper: SwiperCore, realIndex: number) => {
        resetTimer();

        const interval = content[realIndex]?.auto_slide_interval || 5;
        const totalDuration = interval * 1000; // 200ms 추가 제거

        // 백업 타이머 (progress ring 애니메이션이 실패할 경우를 대비)
        timerRef.current = setTimeout(() => {
            moveToNextSlide(swiper, realIndex);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        }, totalDuration);
    }

    const handleSwiperInit = (swiper: SwiperCore) => {
        swiperRef.current = swiper;

        // iOS에서도 동작하도록 초기화 로직 개선
        const initializeAutoSlide = () => {
            if (isInitialized.current) return; // 이미 초기화된 경우 중복 실행 방지
            
            // 영상 재생만 처리하고, progress ring은 handleSlideChange에서 시작
            handleVideoPlayback(0);
            isInitialized.current = true;

            // 로딩 완료 후 첫 슬라이드 변경 이벤트를 트리거하여 progress ring 시작
            setTimeout(() => {
                if (swiper && !swiper.destroyed) {
                    handleSlideChange(swiper);
                }
            }, 100);
        };

        // 첫 번째 영상 로드 완료 후 타이머 시작
        const firstVideo = document.querySelector('.swiper-wrapper video') as HTMLVideoElement;
        if (firstVideo) {
            const onFirstVideoLoaded = () => {
                initializeAutoSlide();
                firstVideo.removeEventListener('loadeddata', onFirstVideoLoaded);
                firstVideo.removeEventListener('canplay', onFirstVideoLoaded);
            };

            if (firstVideo.readyState >= 2) {
                // 이미 완전히 로드된 경우
                setTimeout(() => {
                    initializeAutoSlide();
                }, 100);
            } else {
                // 완전히 로드될 때까지 대기 (여러 이벤트 리스너 추가)
                firstVideo.addEventListener('loadeddata', onFirstVideoLoaded);
                firstVideo.addEventListener('canplay', onFirstVideoLoaded);
                
                // iOS에서 이벤트가 발생하지 않을 경우를 대비한 타임아웃
                setTimeout(() => {
                    if (!isInitialized.current) {
                        console.log('Video load timeout, initializing anyway...');
                        initializeAutoSlide();
                    }
                }, 2000);
            }
        } else {
            // 비디오가 없는 경우 (이미지만 있는 경우)
            setTimeout(() => {
                initializeAutoSlide();
            }, 300);
        }
    }

    const handleSlideChange = (swiper: SwiperCore) => {
        const realIndex = swiper.realIndex;
        const activeIndex = swiper.activeIndex;
        console.log('Slide changed - realIndex:', realIndex, 'activeIndex:', activeIndex, 'total slides:', swiper.slides.length);

        setCurrentSlideIndex(realIndex);

        // 슬라이드 변경 시 즉시 모든 영상을 일시정지하고 처음으로 리셋 (이전 영상이 보이는 것 방지)
        const allVideos = document.querySelectorAll('.swiper-wrapper video');
        allVideos.forEach((video) => {
            const videoElement = video as HTMLVideoElement;
            videoElement.pause();
            videoElement.currentTime = 0;
        });

        // 슬라이드 전환이 완료된 후 영상 재생 처리
        setTimeout(() => {
            handleVideoPlayback(realIndex);
        }, 100);

        // 자동 슬라이드가 활성화된 경우에만 타이머와 progress ring 시작
        // 초기화가 완료된 후에만 progress ring 시작 (로딩 중 중복 시작 방지)
        if (data?.is_auto_slide_enabled && isInitialized.current) {
            // 타이머 시작 (백업용)
            startTimer(swiper, realIndex);

            // progress ring 시작 - 완료 시 슬라이드 이동
            restartProgressAnimation(realIndex, () => {
                moveToNextSlide(swiper, realIndex);
            });
        }
    }

    // 슬라이드 전환 시작 시 모든 영상을 일시정지하고 초기화
    const handleSlideChangeTransitionStart = (swiper: SwiperCore) => {
        // 슬라이드 전환이 시작될 때 즉시 모든 영상을 일시정지하고 처음으로 리셋
        const allVideos = document.querySelectorAll('.swiper-wrapper video');
        allVideos.forEach((video) => {
            const videoElement = video as HTMLVideoElement;
            videoElement.pause();
            videoElement.currentTime = 0;
        });
    }

    // 네비게이션 버튼 클릭 시 마지막 슬라이드에서 첫 번째로 돌아가기
    const handleNavigationClick = (direction: 'next' | 'prev') => {
        if (!swiperRef.current) return;

        const swiper = swiperRef.current;
        const currentIndex = swiper.realIndex;

        // 타이머 리셋
        resetTimer();

        if (direction === 'next' && currentIndex === content.length - 1) {
            // 마지막 슬라이드에서 다음 버튼 클릭 시 첫 번째로
            try {
                swiper.slideTo(0, 300);
                // 슬라이드 변경 후 영상 재생과 progress ring 시작
                setTimeout(() => {
                    handleVideoPlayback(0);
                    if (data?.is_auto_slide_enabled) {
                        startTimer(swiper, 0);
                        restartProgressAnimation(0, () => {
                            moveToNextSlide(swiper, 0);
                        });
                    }
                }, 350); // 슬라이드 전환 완료 후 실행
            } catch (error) {
                // 에러 무시
            }
        } else if (direction === 'prev' && currentIndex === 0) {
            // 첫 번째 슬라이드에서 이전 버튼 클릭 시 마지막으로
            try {
                swiper.slideTo(content.length - 1, 300);
                // 슬라이드 변경 후 영상 재생과 progress ring 시작
                setTimeout(() => {
                    handleVideoPlayback(content.length - 1);
                    if (data?.is_auto_slide_enabled) {
                        startTimer(swiper, content.length - 1);
                        restartProgressAnimation(content.length - 1, () => {
                            moveToNextSlide(swiper, content.length - 1);
                        });
                    }
                }, 350); // 슬라이드 전환 완료 후 실행
            } catch (error) {
                // 에러 무시
            }
        }
    }

    const renderBullet = (index: number, className: string) => {
        const duration = content.length > 0 ? (content[index]?.auto_slide_interval || 5) * 1000 : 5000;
        const isDesktop = device === "DT";
        const center = isDesktop ? "9" : "7";
        const radius = isDesktop ? "8" : "6";

        return `<span class="${className}" style="display: inline-flex">
                    <svg class="progress-ring" style="--duration: ${duration}ms">
                        <circle class="bg" cx="${center}" cy="${center}" r="${radius}" />
                        <circle class="fg" cx="${center}" cy="${center}" r="${radius}" style="stroke-dasharray: ${data?.is_auto_slide_enabled ? '50.24' : '0'}"/>
                    </svg>
                </span>`;
    }

    return (
        <div className="home-top-section-wrapper">
            {content.length > 0 && (device === "DT" ?
                <Swiper
                    key={`swiper-dt-${device}`}
                    onSwiper={handleSwiperInit}
                    autoplay={false}
                    slidesPerView={1}
                    effect="fade"
                    fadeEffect={{
                        crossFade: true
                    }}
                    pagination={{
                        el: '.swiper-pagination',
                        clickable: true,
                        renderBullet: renderBullet,
                    }}
                    navigation={{
                        nextEl: `.swiper-button-next-content.moodwater-juniper-purpose`,
                        prevEl: `.swiper-button-prev-content.moodwater-juniper-purpose`,
                    }}
                    onSlideChange={handleSlideChange}
                    onSlideChangeTransitionStart={handleSlideChangeTransitionStart}
                    onTouchEnd={(swiper) => {
                        const realIndex = swiper.realIndex;
                        // 드래그 거리가 충분할 때만 슬라이드 이동 (클릭 방지)
                        if (Math.abs(swiper.touches.diff) > 50) {
                            // 마지막에서 오른쪽(다음)으로 드래그 → 첫번째로 이동
                            if (
                                realIndex === content.length - 1 &&
                                swiper.touches.diff < 0
                            ) {
                                setTimeout(() => {
                                    swiper.slideTo(0, 300);
                                }, 50);
                            }
                            // 첫번째에서 왼쪽(이전)으로 드래그 → 마지막으로 이동
                            if (
                                realIndex === 0 &&
                                swiper.touches.diff > 0
                            ) {
                                setTimeout(() => {
                                    swiper.slideTo(content.length - 1, 300);
                                }, 50);
                            }
                        }
                    }}
                    threshold={0}
                    updateOnWindowResize={true}
                >
                    {content.map((item, i) => {
                        return (<SwiperSlide key={i}>
                            <div className="swiper-wrapper">
                                {
                                    item.asset.desktop.includes('mp4') ?
                                        <video
                                            key={`video_${i}`}
                                            id={`video_${i}`}
                                            playsInline
                                            muted
                                            loop
                                            preload="metadata"
                                            onLoadedData={(e) => {
                                                const video = e.target as HTMLVideoElement;
                                                video.currentTime = 0;
                                                video.pause();
                                            }}
                                        >
                                            <source
                                                src={item.asset.desktop}
                                                type="video/mp4"
                                            />
                                        </video>
                                        :
                                        <picture>
                                            <source srcSet={item.asset.desktop} media="(min-width: 768px)" />
                                            <img
                                                src={item.asset.mobile} />
                                        </picture>
                                }
                            </div>
                            <div className="content-wrapper" style={{ color: item.title.font_color }}>
                                {item.title.text.trim() !== "" && <div className="title-wrapper">
                                    {item.title.text.split('\n').map((line, index) => <React.Fragment key={index}>{line}<br /></React.Fragment>)}
                                </div>}
                                {item.button.text.trim() !== "" && <div className="button-wrapper"
                                    onClick={() => navigate(item.button.link)}
                                    style={{
                                        border: item.button.is_outlined ? `1px solid ${item.button.outline_color}` : 0,
                                        color: item.button.font_color,
                                        backgroundColor: item.button.background_color
                                    }}
                                >{item.button.text}</div>}
                            </div>
                            {item.is_gradient_enabled && <div className="home-top-gradient"></div>}
                        </SwiperSlide>)
                    })}
                    <div className="swiper-pagination" id={'home-top-swiper-pagination-id'} />
                    {/* 네비게이션 버튼에 클릭 이벤트 추가 */}
                    <div
                        className="swiper-button-next-content moodwater-juniper-purpose"
                        onClick={() => handleNavigationClick('next')}
                    />
                    <div
                        className="swiper-button-prev-content moodwater-juniper-purpose"
                        onClick={() => handleNavigationClick('prev')}
                    />
                </Swiper> :
                <Swiper
                    key={`swiper-mobile-${device}`}
                    onSwiper={handleSwiperInit}
                    autoplay={false}
                    slidesPerView={1}
                    effect="fade"
                    fadeEffect={{
                        crossFade: true
                    }}
                    pagination={{
                        el: '.swiper-pagination',
                        clickable: true,
                        renderBullet: renderBullet,
                    }}
                    navigation={{
                        nextEl: `.swiper-button-next-content.moodwater-juniper-purpose`,
                        prevEl: `.swiper-button-prev-content.moodwater-juniper-purpose`,
                    }}
                    onSlideChange={handleSlideChange}
                    onSlideChangeTransitionStart={handleSlideChangeTransitionStart}
                    onTouchEnd={(swiper) => {
                        const realIndex = swiper.realIndex;
                        // 드래그 거리가 충분할 때만 슬라이드 이동 (클릭 방지)
                        if (Math.abs(swiper.touches.diff) > 50) {
                            // 마지막에서 오른쪽(다음)으로 드래그 → 첫번째로 이동
                            if (
                                realIndex === content.length - 1 &&
                                swiper.touches.diff < 0
                            ) {
                                setTimeout(() => {
                                    swiper.slideTo(0, 300);
                                }, 50);
                            }
                            // 첫번째에서 왼쪽(이전)으로 드래그 → 마지막으로 이동
                            if (
                                realIndex === 0 &&
                                swiper.touches.diff > 0
                            ) {
                                setTimeout(() => {
                                    swiper.slideTo(content.length - 1, 300);
                                }, 50);
                            }
                        }
                    }}
                    threshold={0}
                    updateOnWindowResize={true}
                >
                    {content.map((item, i) => {
                        return (<SwiperSlide key={i}>
                            <div className="swiper-wrapper">
                                {
                                    item.asset.mobile.includes('mp4') ?
                                        <video
                                            key={`video_${i}`}
                                            id={`video_${i}`}
                                            playsInline
                                            muted
                                            loop
                                            preload="metadata"
                                            onLoadedData={(e) => {
                                                const video = e.target as HTMLVideoElement;
                                                video.currentTime = 0;
                                                video.pause();
                                            }}
                                        >
                                            <source
                                                src={item.asset.mobile}
                                                type="video/mp4"
                                            />
                                        </video>
                                        :
                                        <picture>
                                            <source srcSet={item.asset.mobile} media="(min-width: 768px)" />
                                            <img
                                                src={item.asset.mobile} />
                                        </picture>
                                }
                            </div>
                            {item.is_gradient_enabled && <div className="home-top-gradient"></div>}
                            <div className="content-wrapper" style={{ color: item.title.font_color }}>
                                {item.title.text.trim() !== "" && <div className="title-wrapper">
                                    {item.title.text.split('\n').map((line, index) => <React.Fragment key={index}>{line}<br /></React.Fragment>)}
                                </div>}
                                {item.button.text.trim() !== "" && <div className="button-wrapper"
                                    onClick={() => navigate(item.button.link)}
                                    style={{
                                        border: item.button.is_outlined ? `1px solid ${item.button.outline_color}` : 0,
                                        color: item.button.font_color,
                                        backgroundColor: item.button.background_color
                                    }}
                                >{item.button.text}</div>}
                            </div>
                        </SwiperSlide>)
                    })}
                    <div className="swiper-pagination" id={'home-top-swiper-pagination-id'} />
                    {/* 네비게이션 버튼에 클릭 이벤트 추가 */}
                    <div
                        className="swiper-button-next-content moodwater-juniper-purpose"
                        onClick={() => handleNavigationClick('next')}
                    />
                    <div
                        className="swiper-button-prev-content moodwater-juniper-purpose"
                        onClick={() => handleNavigationClick('prev')}
                    />
                </Swiper>
            )}
        </div>
    )
}

export default HomeTopSection